import {Tooltip} from 'platform/components';
import {
  Box,
  VStack,
  HStack,
  Center,
  Clickable,
  Icon,
  getColorByPath,
  useDevice,
  Space,
} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {or} from 'ramda';

import {ActivitySidebarAction} from '../types/ActivitySidebarAction';

interface ActivitySidebarMenuProps {
  activeTab: string | null;
  actions: ActivitySidebarAction[];
  onClick: (tabId: string) => void;
}

export function ActivitySidebarMenu(props: ActivitySidebarMenuProps) {
  const device = useDevice();
  const isMobileOrTablet = or(device === 'mobile', device === 'tablet');
  const items = props.actions.map((action) => (
    <Tooltip key={action.id} label={action.label} placement="left">
      <Clickable
        isDisabled={action.disabled}
        onClick={() => props.onClick(action.id)}
        data-testid={action['data-testid']}
      >
        <HoverableBox isActive={props.activeTab === action.id} disabled={action.disabled}>
          <Center width="100%" height="100%">
            {action.indicator}
            <Icon size={5} value={action.icon} color="palettes.neutral.800.100" />
          </Center>
        </HoverableBox>
      </Clickable>
    </Tooltip>
  ));

  return match(isMobileOrTablet)
    .with(true, () => (
      <>
        <Space vertical={20} />
        <Box
          width="100%"
          position="fixed"
          zIndex="CONTENT"
          bottom={0}
          left={0}
          right={0}
          padding={2}
          backgroundColor="general.white"
        >
          <HStack spacing={4}>{items}</HStack>
        </Box>
      </>
    ))
    .otherwise(() => (
      <Box width={12} padding={2} backgroundColor="general.white" height="100%" zIndex="CONTENT">
        <Box position="sticky" top={2}>
          <VStack spacing={4}>{items}</VStack>
        </Box>
      </Box>
    ));
}

interface HoverableBoxProps {
  isActive: boolean;
  disabled?: boolean;
}

const HoverableBox = styled.div<HoverableBoxProps>`
  position: relative;
  height: ${({theme}) => theme.getSize(8)};
  width: ${({theme}) => theme.getSize(8)};
  padding: ${({theme}) => theme.getSize(2)};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme, isActive}) =>
    isActive ? theme.colors.palettes.blue[20][100] : getColorByPath('general.white', theme)};
  opacity: ${({disabled}) => (disabled ? 0.4 : 1)};

  &:hover {
    background-color: ${({theme, isActive}) =>
      !isActive ? theme.colors.palettes.neutral[20][100] : undefined};
  }
`;
